import { useLocalStorageState } from 'ahooks';
import { useCallback } from 'react';
import { SITE } from '@/app/constants';
import { getTodayDateString } from '@/app/utils';
import { useUserNexus } from './use-user-nexus';

const WATERMARK_INTERSTITIAL_SHOWN_DATE_KEY = 'vidu_watermark_interstitial_shown_date';

/**
 * 插页由 init-interstitial 在 GPT 加载时统一定义并 display()。
 * 本 hook：仅广告覆盖国家 + 每天只展示一次；当天已展示后「带水印下载」以普通按钮渲染。
 */
export interface UseInterstitialAdReturn {
  /** 带水印下载时调用：符合条件则记「今日已展示」一次 */
  markWatermarkInterstitialShown: (isWithWatermark: boolean) => void;
  /** 今日是否还可展示（用于决定是否以锚点渲染，触发展示） */
  canShowWatermarkInterstitialToday: boolean;
}

export const useInterstitialAd = (): UseInterstitialAdReturn => {
  const { isMissionWatchAdCredit } = useUserNexus();
  const [lastShownDate, setLastShownDate] = useLocalStorageState<string>(WATERMARK_INTERSTITIAL_SHOWN_DATE_KEY, {
    defaultValue: '',
  });

  const isEligible = SITE === 'global' && isMissionWatchAdCredit;
  const canShowWatermarkInterstitialToday = isEligible && lastShownDate !== getTodayDateString();

  const markWatermarkInterstitialShown = useCallback(
    (isWithWatermark: boolean) => {
      if (!isWithWatermark) return;
      if (!isEligible) return;
      const today = getTodayDateString();
      if (lastShownDate === today) return;
      setLastShownDate(today);
    },
    [isEligible, lastShownDate, setLastShownDate],
  );

  return {
    markWatermarkInterstitialShown,
    canShowWatermarkInterstitialToday,
  };
};
